import React, { useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { X, Upload, Sparkles, Linkedin } from 'lucide-react';
import { useResume } from '../../context/ResumeContext';
import { motion, AnimatePresence } from 'framer-motion';
import { AppleLoader } from '@/components/ui/AppleLoader';
import { toast } from 'sonner';
import { generateWithAI } from '@/services/aiGenerate';
import {
  beginLinkedInImport,
  consumeLinkedInImportResult,
  isLinkedInImportConfigured,
} from '@/services/linkedinImport';

export const PersonalDetailsForm = () => {
  const { resumeData, updatePersonal } = useResume();
  const { personal } = resumeData;
  const fileInputRef = useRef(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [linkedinLoading, setLinkedinLoading] = useState(false);
  const importApplied = useRef(false);

  const handleChange = (field, value) => {
    updatePersonal({ [field]: value });
  };

  // Apply LinkedIn OAuth result after redirect back to the builder
  useEffect(() => {
    if (importApplied.current) return;
    const imported = consumeLinkedInImportResult();
    if (!imported) return;
    importApplied.current = true;

    const patch = {};
    if (imported.fullName) patch.fullName = imported.fullName;
    if (imported.email) patch.email = imported.email;
    if (imported.picture) patch.picture = imported.picture;
    if (imported.title) patch.title = imported.title;
    if (imported.linkedin) patch.linkedin = imported.linkedin;

    if (Object.keys(patch).length) {
      updatePersonal(patch);
      toast.message('Add your job title, summary, and experience to finish.');
    }
  }, [updatePersonal]);

  const handleLinkedInImport = () => {
    if (!isLinkedInImportConfigured()) {
      toast.error('LinkedIn import is temporarily unavailable. Please try again later.');
      return;
    }
    try {
      setLinkedinLoading(true);
      beginLinkedInImport();
    } catch (err) {
      setLinkedinLoading(false);
      toast.error(err.message || 'Could not start LinkedIn import.');
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      handleChange('picture', base64String);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    handleChange('picture', '');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleGenerateSummary = async () => {
    if (!personal.title || personal.title.trim() === '') {
      toast.error('Enter your professional title first to generate a summary.');
      return;
    }

    try {
      setIsGenerating(true);
      const generatedSummary = await generateWithAI({
        task: 'summary',
        fields: {
          title: personal.title,
          location: personal.location || '',
        },
      });
      handleChange('summary', generatedSummary);
      toast.success('Summary generated');
    } catch (err) {
      console.error('Error generating summary:', err);
      toast.error(err.message || 'Could not generate summary. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Card className="p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-foreground mb-2">Personal Details</h2>
        <p className="text-sm text-muted-foreground">Tell us about yourself. This information will appear at the top of your resume.</p>
      </div>

      <div className="rounded-xl border border-[#0A66C2]/25 bg-[#0A66C2]/5 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-medium text-foreground">Import from LinkedIn</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Sign in with LinkedIn to fill your name, email, and photo.
            </p>
          </div>
          <Button
            type="button"
            onClick={handleLinkedInImport}
            disabled={linkedinLoading}
            className="shrink-0 bg-[#0A66C2] hover:bg-[#004182] text-white"
          >
            {linkedinLoading ? (
              <AppleLoader size={14} tone="light" label="Redirecting" labelClassName="text-white" />
            ) : (
              <>
                <Linkedin className="w-4 h-4 mr-2" />
                Continue with LinkedIn
              </>
            )}
          </Button>
        </div>
      </div>
      
      {/* Profile Picture Upload */}
      <div className="space-y-2">
        <Label>Profile Picture (Optional)</Label>
        <div className="flex items-center gap-4">
          {personal.picture ? (
            <div className="relative">
              <img
                src={personal.picture}
                alt="Profile"
                className="w-24 h-24 rounded-full object-cover border-2 border-gray-200"
              />
              <button
                type="button"
                onClick={handleRemoveImage}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                aria-label="Remove picture"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="w-24 h-24 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center bg-gray-50">
              <Upload className="w-8 h-8 text-gray-400" />
            </div>
          )}
          <div className="flex-1">
            <Input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="cursor-pointer"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Recommended: Square image (400x400 pixels or 500x500 pixels), max 5MB. JPG, PNG, or GIF.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="fullName">Full Name *</Label>
          <Input
            id="fullName"
            placeholder="John Doe"
            value={personal.fullName}
            onChange={(e) => handleChange('fullName', e.target.value)}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="title">Professional Title *</Label>
          <Input
            id="title"
            placeholder="Software Engineer"
            value={personal.title}
            onChange={(e) => handleChange('title', e.target.value)}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="email">Email *</Label>
          <Input
            id="email"
            type="email"
            placeholder="john.doe@example.com"
            value={personal.email}
            onChange={(e) => handleChange('email', e.target.value)}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="phone">Phone *</Label>
          <Input
            id="phone"
            type="tel"
            placeholder="(555) 123-4567"
            value={personal.phone}
            onChange={(e) => handleChange('phone', e.target.value)}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="location">Location</Label>
          <Input
            id="location"
            placeholder="New York, NY"
            value={personal.location}
            onChange={(e) => handleChange('location', e.target.value)}
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="linkedin">LinkedIn</Label>
          <Input
            id="linkedin"
            placeholder="linkedin.com/in/johndoe"
            value={personal.linkedin}
            onChange={(e) => handleChange('linkedin', e.target.value)}
          />
        </div>
        
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="website">Website / Portfolio</Label>
          <Input
            id="website"
            placeholder="johndoe.com"
            value={personal.website}
            onChange={(e) => handleChange('website', e.target.value)}
          />
        </div>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="summary">
          Professional Summary{" "}
        </Label>
        <button
            type="button"
            className="ml-2 text-xs flex gap-2 items-center no-underline text-decoration-none text-blue-600 hover:text-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            onClick={handleGenerateSummary}
            disabled={isGenerating || !personal.title || personal.title.trim() === ''}
          >
            Generate with AI <Sparkles className="w-4 h-4" />
          </button>
        <div className="relative">
          <AnimatePresence>
            {isGenerating && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-100/30 to-transparent pointer-events-none rounded-md z-10"
                style={{
                  background: "linear-gradient(90deg, transparent 0%, rgba(59, 130, 246, 0.1) 50%, transparent 100%)",
                  backgroundSize: "200% 100%",
                  animation: "shimmer 2s infinite",
                }}
              />
            )}
          </AnimatePresence>
          <motion.div
            animate={
              isGenerating
                ? {
                    boxShadow: [
                      "0 0 0 0px rgba(59, 130, 246, 0.2)",
                      "0 0 0 3px rgba(59, 130, 246, 0.1)",
                      "0 0 0 0px rgba(59, 130, 246, 0.2)",
                    ],
                  }
                : {}
            }
            transition={{
              duration: 2,
              repeat: isGenerating ? Infinity : 0,
              ease: "easeInOut",
            }}
            className="rounded-md"
          >
            <Textarea
              id="summary"
              placeholder={
                isGenerating
                  ? ""
                  : "A brief summary of your professional background, skills, and career objectives..."
              }
              className={`min-h-[120px] relative transition-all ${
                isGenerating
                  ? "border-blue-300 bg-blue-50/30"
                  : ""
              }`}
              value={personal.summary}
              onChange={(e) => handleChange('summary', e.target.value)}
              disabled={isGenerating}
            />
          </motion.div>
          {isGenerating && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className="absolute bottom-4 left-4 flex items-center gap-2 text-sm font-medium text-blue-600 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-sm border border-blue-100"
            >
              <AppleLoader size={14} tone="primary" />
              <motion.span
                animate={{
                  opacity: [0.6, 1, 0.6],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              >
                AI is crafting your summary
              </motion.span>
            </motion.div>
          )}
        </div>
        <p className="text-xs text-muted-foreground">2-3 sentences highlighting your expertise and what you bring to the role</p>
        <p className="text-xs text-muted-foreground mt-1">
          <span className="font-medium">Tip:</span> Enter your professional title, then click "Generate with AI" to create a personalized summary.
        </p>
      </div>
    </Card>
  );
}